import { useEffect, useRef } from "react";

type LangCode = string;

const LANG_MAP: Record<string, string> = {
  pt: "pt-PT",
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
};

const PHRASES = {
  pt: {
    in: (d: string) => `Em ${d}, ${""}`,
    arrived: (n: string) => `Chegou a ${n}`,
    starting: (n: string) => `A iniciar navegação para ${n}`,
  },
  en: {
    in: (d: string) => `In ${d}, `,
    arrived: (n: string) => `You have arrived at ${n}`,
    starting: (n: string) => `Starting navigation to ${n}`,
  },
  es: {
    in: (d: string) => `En ${d}, `,
    arrived: (n: string) => `Has llegado a ${n}`,
    starting: (n: string) => `Iniciando navegación hacia ${n}`,
  },
  fr: {
    in: (d: string) => `Dans ${d}, `,
    arrived: (n: string) => `Vous êtes arrivé à ${n}`,
    starting: (n: string) => `Démarrage de la navigation vers ${n}`,
  },
} as const;

function speak(text: string, lang: string) {
  if (typeof window === "undefined") return;
  const synth = window.speechSynthesis;
  if (!synth) return;
  try {
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = LANG_MAP[lang] ?? lang;
    u.rate = 1;
    u.pitch = 1;
    u.volume = 1;
    synth.speak(u);
  } catch {
    /* ignore */
  }
}

export function useVoiceNavigation({
  enabled,
  lang,
  navigationActive,
  activeStopName,
  activeStopIdx,
  currentStepText,
  distanceMeters,
}: {
  enabled: boolean;
  lang: LangCode;
  navigationActive: boolean;
  activeStopName: string | null | undefined;
  activeStopIdx: number;
  currentStepText: string | null | undefined;
  distanceMeters: number | null;
}) {
  const lastStepRef = useRef<string | null>(null);
  const announcedThresholdsRef = useRef<Set<number>>(new Set());
  const arrivedAnnouncedRef = useRef(false);
  const lastStopIdxRef = useRef<number>(-1);
  const phrases = (PHRASES as any)[lang] ?? PHRASES.en;

  // Reset state when navigation toggles or stop changes
  useEffect(() => {
    if (!navigationActive) {
      lastStepRef.current = null;
      announcedThresholdsRef.current.clear();
      arrivedAnnouncedRef.current = false;
      lastStopIdxRef.current = -1;
      if (typeof window !== "undefined") window.speechSynthesis?.cancel();
      return;
    }
    if (lastStopIdxRef.current !== activeStopIdx) {
      lastStopIdxRef.current = activeStopIdx;
      lastStepRef.current = null;
      announcedThresholdsRef.current.clear();
      arrivedAnnouncedRef.current = false;
      if (enabled && activeStopName) {
        speak(phrases.starting(activeStopName), lang);
      }
    }
  }, [navigationActive, activeStopIdx, enabled, activeStopName, lang, phrases]);

  // Stop any ongoing speech the moment voice is muted.
  useEffect(() => {
    if (!enabled && typeof window !== "undefined") {
      window.speechSynthesis?.cancel();
      lastStepRef.current = null;
      announcedThresholdsRef.current.clear();
    }
  }, [enabled]);

  // Speak new step instruction when it changes
  useEffect(() => {
    if (!enabled || !navigationActive) return;
    const step = (currentStepText ?? "").trim();
    if (!step || step === lastStepRef.current) return;
    lastStepRef.current = step;
    speak(step, lang);
  }, [enabled, navigationActive, currentStepText, lang]);

  // Distance-based announcements + arrival
  useEffect(() => {
    if (!enabled || !navigationActive || distanceMeters == null) return;
    if (distanceMeters < 40 && !arrivedAnnouncedRef.current && activeStopName) {
      arrivedAnnouncedRef.current = true;
      speak(phrases.arrived(activeStopName), lang);
      return;
    }
    const thresholds = [500, 200, 100, 50];
    for (const t of thresholds) {
      if (distanceMeters <= t && !announcedThresholdsRef.current.has(t)) {
        announcedThresholdsRef.current.add(t);
        const step = lastStepRef.current ?? "";
        const dText = t >= 1000 ? `${t / 1000} km` : `${t} m`;
        speak(`${phrases.in(dText)}${step}`, lang);
        break;
      }
    }
  }, [enabled, navigationActive, distanceMeters, activeStopName, lang, phrases]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") window.speechSynthesis?.cancel();
    };
  }, []);
}
